// Company tags on the tender list: the "Your tags" filter in the filter
// box, the badges on each row and the line in the detail panel. Logic in
// companyTags.ts; the manager is CompanyTagsDialog.tsx. (Not named
// CompanyTags.tsx: on a case-insensitive disk that import would resolve to
// companyTags.ts.)
import React from 'react';
import { Box, Button, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

import { rowTagBadges, tagEvidence } from './companyTags';
import type { CompanyTag, TenderTagHit } from './companyTags';

const MANAGE = '__manage__';

export const CompanyTagFilter: React.FC<{
    tags: CompanyTag[];
    counts: Record<string, number>;
    selected: string[];
    onChange: (selected: string[]) => void;
    onManage: () => void;
    keywordNote: string | null;  // keywords that match nothing right now, named (companyTags.keywordProblem)
    fontSize: string;
}> = ({ tags, counts, selected, onChange, onManage, keywordNote, fontSize }) => {
    if (tags.length === 0) {
        return (
            <Button size="small" startIcon={<LocalOfferOutlinedIcon fontSize="small" />} onClick={onManage}
                sx={{ mt: 1, textTransform: 'none', fontSize, alignSelf: 'flex-start' }}>
                Create your own tags…
            </Button>
        );
    }
    const nameOf = (id: string) => tags.find(t => t.tagId === id)?.name ?? '';
    return (
        <FormControl fullWidth size="small" sx={{ mt: 1.25, bgcolor: 'background.paper' }}>
            <InputLabel sx={{ fontSize }}>Your tags</InputLabel>
            <Select
                multiple
                value={selected}
                label="Your tags"
                onChange={(e) => {
                    const raw = e.target.value;
                    const values = typeof raw === 'string' ? raw.split(',') : raw;
                    if (values.includes(MANAGE)) {
                        onManage();
                        return;
                    }
                    onChange(values);
                }}
                renderValue={(values) => values.map(nameOf).join(', ')}
                sx={{ fontSize }}
                MenuProps={{ PaperProps: { style: { maxHeight: 380 } } }}
            >
                {tags.map(tag => (
                    <MenuItem key={tag.tagId} value={tag.tagId} dense>
                        <Checkbox size="small" checked={selected.includes(tag.tagId)} sx={{ p: 0.25, mr: 0.5 }} />
                        <ListItemText primary={`${tag.name} (${counts[tag.tagId] ?? 0})`}
                            primaryTypographyProps={{ fontSize: '0.78rem' }} />
                    </MenuItem>
                ))}
                <MenuItem value={MANAGE} dense sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <ListItemText
                        primary="Manage tags…"
                        secondary={keywordNote ?? undefined}
                        primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: '0.65rem' }} />
                </MenuItem>
            </Select>
        </FormControl>
    );
};

// "Yours", not SIMAP's: filled secondary colour with a tag icon, unlike the
// outlined SIMAP classification badges next to them.
const badgeSx = {
    display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 0.6, borderRadius: 1,
    bgcolor: 'secondary.main', color: 'secondary.contrastText', fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.6,
};

export const CompanyTagBadges: React.FC<{ hits: TenderTagHit[] | undefined }> = ({ hits }) => {
    const { shown, more } = rowTagBadges(hits);
    if (shown.length === 0) return null;
    return (
        <>
            {shown.map(h => (
                <Tooltip key={h.tag.tagId} title={tagEvidence(h.match)} arrow>
                    <Box component="span" sx={badgeSx}>
                        <LocalOfferOutlinedIcon sx={{ fontSize: '0.7rem' }} />
                        {h.tag.name}
                    </Box>
                </Tooltip>
            ))}
            {more > 0 && (
                <Tooltip title={(hits ?? []).slice(shown.length).map(h => h.tag.name).join(', ')} arrow>
                    <Box component="span" sx={{ ...badgeSx, bgcolor: 'action.selected', color: 'text.primary' }}>
                        +{more}
                    </Box>
                </Tooltip>
            )}
        </>
    );
};

export const CompanyTagDetail: React.FC<{ hits: TenderTagHit[] | undefined }> = ({ hits }) => {
    if (!hits || hits.length === 0) return null;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
            <Typography component="span" variant="body2"
                sx={{ fontWeight: 'medium', color: 'text.secondary', fontSize: '0.75rem' }}>
                Your tags:
            </Typography>
            {hits.map(h => (
                <Chip key={h.tag.tagId} size="small" color="secondary" icon={<LocalOfferOutlinedIcon />}
                    label={`${h.tag.name} · ${tagEvidence(h.match)}`}
                    sx={{ height: 22, fontSize: '0.68rem' }} />
            ))}
        </Box>
    );
};
